import { Component } from 'react';
import type { ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

/**
 * Catches render-time errors anywhere in the tree and shows a friendly fallback
 * instead of a blank white page (React unmounts the whole tree on an uncaught
 * render error, which otherwise leaves users staring at nothing until they
 * refresh). The "Reload" button does the manual refresh for them.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, message: error instanceof Error ? error.message : 'Unexpected error' };
  }

  componentDidCatch(error: unknown, info: unknown) {
    console.error('[ErrorBoundary] Caught a render error:', error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-6">
            We hit an unexpected error displaying this page. Reloading usually fixes it.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-block bg-coastal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-coastal-700 transition-colors"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
